import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

const JobApplicationForm = ({ job, onClose }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    position: job?.title || '',
    experience: '',
    cv: null,
    coverLetter: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.fullName.trim()) {
      newErrors.fullName = t('careers.application.validation.required');
    }
    
    if (!formData.email.trim()) {
      newErrors.email = t('careers.application.validation.required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('careers.application.validation.invalidEmail');
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = t('careers.application.validation.required');
    } else if (!/^[\+]?[0-9\s\-\(\)]{10,}$/.test(formData.phone)) {
      newErrors.phone = t('careers.application.validation.invalidPhone');
    }
    
    if (!formData.position.trim()) {
      newErrors.position = t('careers.application.validation.required');
    }
    
    if (!formData.experience.trim()) {
      newErrors.experience = t('careers.application.validation.required');
    }
    
    if (!formData.coverLetter.trim()) {
      newErrors.coverLetter = t('careers.application.validation.required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({
          ...prev,
          cv: 'Dosya boyutu 5MB\'dan küçük olmalıdır'
        }));
        return;
      }
      
      // Check file type
      const allowedTypes = ['.pdf', '.doc', '.docx'];
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      if (!allowedTypes.includes(fileExtension)) {
        setErrors(prev => ({
          ...prev,
          cv: 'Sadece PDF, DOC ve DOCX dosyaları kabul edilir'
        }));
        return;
      }
      
      setFormData(prev => ({
        ...prev,
        cv: file
      }));
      
      if (errors.cv) {
        setErrors(prev => ({
          ...prev,
          cv: ''
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key]) {
          formDataToSend.append(key, formData[key]);
        }
      });
      
      // Add job ID if available
      if (job?.id) {
        formDataToSend.append('jobId', job.id);
      }
      
      const response = await axios.post('/api/job-application', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.status === 'success') {
        setSubmitStatus('success');
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Application submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const modalVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const formVariants = {
    hidden: { opacity: 0, scale: 0.9, y: 50 },
    visible: { 
      opacity: 1, 
      scale: 1, 
      y: 0,
      transition: { duration: 0.3 }
    },
    exit: { 
      opacity: 0, 
      scale: 0.9, 
      y: 50,
      transition: { duration: 0.2 }
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
        variants={modalVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        onClick={onClose}
      >
        <motion.div
          className="bg-gray-900 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          variants={formVariants}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-6 border-b border-gray-700">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-white">
                  {t('careers.application.title')}
                </h2>
                <p className="text-gray-300 mt-1">
                  {job?.title || 'Genel Başvuru'}
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6">
            <div className="space-y-6">
              {/* Personal Information */}
              <div>
                <h3 className="text-lg font-semibold text-cyan-400 mb-4">
                  {t('careers.application.form.personalInfo')}
                </h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Full Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t('careers.application.form.fullName')} *
                    </label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 bg-gray-800 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-colors ${
                        errors.fullName ? 'border-red-500' : 'border-gray-600 focus:border-cyan-500'
                      }`}
                      placeholder="Adınızı ve soyadınızı giriniz"
                    />
                    {errors.fullName && (
                      <p className="text-red-400 text-sm mt-1">{errors.fullName}</p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t('careers.application.form.email')} *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 bg-gray-800 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-colors ${
                        errors.email ? 'border-red-500' : 'border-gray-600 focus:border-cyan-500'
                      }`}
                      placeholder="ornek@email.com"
                    />
                    {errors.email && (
                      <p className="text-red-400 text-sm mt-1">{errors.email}</p>
                    )}
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t('careers.application.form.phone')} *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 bg-gray-800 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-colors ${
                        errors.phone ? 'border-red-500' : 'border-gray-600 focus:border-cyan-500'
                      }`}
                      placeholder="+90 555 123 45 67"
                    />
                    {errors.phone && (
                      <p className="text-red-400 text-sm mt-1">{errors.phone}</p>
                    )}
                  </div>

                  {/* Position */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {t('careers.application.form.position')} *
                    </label>
                    <input
                      type="text"
                      name="position"
                      value={formData.position}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 bg-gray-800 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-colors ${
                        errors.position ? 'border-red-500' : 'border-gray-600 focus:border-cyan-500'
                      }`}
                      placeholder="Başvurduğunuz pozisyon"
                      readOnly={job?.title}
                    />
                    {errors.position && (
                      <p className="text-red-400 text-sm mt-1">{errors.position}</p>
                    )}
                  </div>
                </div>

                {/* Experience */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t('careers.application.form.experience')} *
                  </label>
                  <select
                    name="experience"
                    value={formData.experience}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 bg-gray-800 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-colors ${
                      errors.experience ? 'border-red-500' : 'border-gray-600 focus:border-cyan-500'
                    }`}
                  >
                    <option value="">Deneyim seviyenizi seçiniz</option>
                    <option value="0-1">0-1 yıl</option>
                    <option value="1-3">1-3 yıl</option>
                    <option value="3-5">3-5 yıl</option>
                    <option value="5-10">5-10 yıl</option>
                    <option value="10+">10+ yıl</option>
                  </select>
                  {errors.experience && (
                    <p className="text-red-400 text-sm mt-1">{errors.experience}</p>
                  )}
                </div>

                {/* CV Upload */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t('careers.application.form.cv')}
                  </label>
                  <div className="relative">
                    <input
                      type="file"
                      name="cv"
                      onChange={handleFileChange}
                      accept=".pdf,.doc,.docx"
                      className="hidden"
                      id="cv-upload"
                    />
                    <label
                      htmlFor="cv-upload"
                      className={`w-full px-4 py-3 bg-gray-800 border border-dashed rounded-lg text-center cursor-pointer hover:bg-gray-700 transition-colors ${
                        errors.cv ? 'border-red-500' : 'border-gray-600 hover:border-cyan-500'
                      }`}
                    >
                      <div className="flex flex-col items-center">
                        <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                        <span className="text-gray-300">
                          {formData.cv ? formData.cv.name : 'CV yüklemek için tıklayın'}
                        </span>
                        <span className="text-sm text-gray-500 mt-1">
                          PDF, DOC, DOCX (Max 5MB)
                        </span>
                      </div>
                    </label>
                  </div>
                  {errors.cv && (
                    <p className="text-red-400 text-sm mt-1">{errors.cv}</p>
                  )}
                </div>

                {/* Cover Letter */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    {t('careers.application.form.coverLetter')} *
                  </label>
                  <textarea
                    name="coverLetter"
                    value={formData.coverLetter}
                    onChange={handleInputChange}
                    rows={6}
                    className={`w-full px-4 py-3 bg-gray-800 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-colors resize-none ${
                      errors.coverLetter ? 'border-red-500' : 'border-gray-600 focus:border-cyan-500'
                    }`}
                    placeholder={t('careers.application.form.coverLetterPlaceholder')}
                  />
                  {errors.coverLetter && (
                    <p className="text-red-400 text-sm mt-1">{errors.coverLetter}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Submit Status */}
            {submitStatus && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`mt-4 p-4 rounded-lg ${
                  submitStatus === 'success' 
                    ? 'bg-green-800 text-green-200 border border-green-600' 
                    : 'bg-red-800 text-red-200 border border-red-600'
                }`}
              >
                {submitStatus === 'success' 
                  ? t('careers.application.form.success')
                  : t('careers.application.form.error')
                }
              </motion.div>
            )}

            {/* Submit Button */}
            <div className="mt-8 flex gap-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                İptal
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-cyan-500 to-cyan-600 text-black font-semibold rounded-lg hover:from-cyan-400 hover:to-cyan-500 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting 
                  ? t('careers.application.form.sending')
                  : t('careers.application.form.submit')
                }
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default JobApplicationForm;